import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Card, Switch } from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";

const AI_ASSIGNMENT_KEY = "ai_assignment_enabled";
const OPENAI_ASSISTANT_KEY = "openai_assistant_enabled";

const AiAssignmentSettings: React.FC = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [assignmentOn, setAssignmentOn] = useState<boolean | null>(null);
  const [openAiAssistantOn, setOpenAiAssistantOn] = useState<boolean | null>(
    null
  );
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [savingOpenAi, setSavingOpenAi] = useState(false);

  const loadSettings = useCallback(async () => {
    const [a, o] = await Promise.allSettled([
      axios.get(`/settings/${AI_ASSIGNMENT_KEY}`),
      axios.get(`/settings/${OPENAI_ASSISTANT_KEY}`),
    ]);
    if (a.status === "fulfilled") {
      setAssignmentOn(String(a.value.data?.setting_value) === "1");
    } else {
      console.error("Failed to load assignment setting", a.reason);
      setAssignmentOn(true);
    }
    if (o.status === "fulfilled") {
      setOpenAiAssistantOn(String(o.value.data?.setting_value) === "1");
    } else {
      console.error("Failed to load OpenAI assistant setting", o.reason);
      setOpenAiAssistantOn(true);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleAssignmentToggle = async (checked: boolean) => {
    setSavingAssignment(true);
    try {
      await axios.post(`/settings/${AI_ASSIGNMENT_KEY}`, {
        value: checked ? "1" : "0",
      });
      setAssignmentOn(checked);
      fetchSuccess(
        checked
          ? "AI auto-assignment enabled"
          : "AI auto-assignment paused"
      );
    } catch (err: unknown) {
      fetchError("Failed to update setting");
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleOpenAiAssistantToggle = async (checked: boolean) => {
    setSavingOpenAi(true);
    try {
      await axios.post(`/settings/${OPENAI_ASSISTANT_KEY}`, {
        value: checked ? "1" : "0",
      });
      setOpenAiAssistantOn(checked);
      fetchSuccess(
        checked
          ? "OpenAI AI assistant is enabled"
          : "OpenAI AI assistant is paused"
      );
    } catch (err: unknown) {
      fetchError("Failed to update setting");
    } finally {
      setSavingOpenAi(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{
          padding: "24px",
          margin: "16px auto",
          maxWidth: "900px",
        }}
      >
        <SoftTypography variant="h5" sx={{ mb: 1 }}>
          AI assistant
        </SoftTypography>
        <SoftTypography variant="body2" color="text" sx={{ mb: 3 }}>
          Control AI ticket assignment and, when the server uses OpenAI, the
          in-app chat assistant for customers and employees.
        </SoftTypography>

        <SoftTypography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
          Ticket assignment
        </SoftTypography>
        <SoftTypography variant="body2" color="text" sx={{ mb: 2 }}>
          When enabled, newly created tickets are automatically routed to the
          best available engineer based on skill match, workload and urgency.
          Medium-confidence assignments are flagged for supervisor review; low
          confidence falls back to manual assignment.
        </SoftTypography>

        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            mb: 4,
          }}
        >
          <Box>
            <SoftTypography variant="button" fontWeight="bold">
              AI auto-assignment
            </SoftTypography>
          </Box>
          <Switch
            checked={!!assignmentOn}
            disabled={assignmentOn === null || savingAssignment}
            onChange={(e) => handleAssignmentToggle(e.target.checked)}
            color="primary"
          />
        </SoftBox>

        {assignmentOn === false && (
          <Alert severity="warning" sx={{ mt: -2, mb: 3 }}>
            AI assignment is paused. All new tickets require manual assignment.
          </Alert>
        )}

        <SoftTypography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
          OpenAI chat assistant
        </SoftTypography>
        <SoftTypography variant="body2" color="text" sx={{ mb: 2 }}>
          When your server is configured to use the OpenAI provider,
        this switch turns the in-app AI assistant
          (customer and employee chat) on or off. When off, users see a
          &quot;temporarily unavailable&quot; message instead of calling OpenAI.
          Deployments that use a local Ollama model are not affected.
        </SoftTypography>

        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          <Box>
            <SoftTypography variant="button" fontWeight="bold">
              Use OpenAI for AI assistant
            </SoftTypography>
          </Box>
          <Switch
            checked={!!openAiAssistantOn}
            disabled={openAiAssistantOn === null || savingOpenAi}
            onChange={(e) => handleOpenAiAssistantToggle(e.target.checked)}
            color="primary"
          />
        </SoftBox>

        {openAiAssistantOn === false && (
          <Alert severity="info" sx={{ mt: 3 }}>
            The AI chat assistant will not call OpenAI until you turn this back
            on. Ticket auto-assignment is controlled in the section above.
          </Alert>
        )}
      </Card>
    </DashboardLayout>
  );
};

export default AiAssignmentSettings;
